import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button, Collapse, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import faqData from '../assets/faq.json';
import { useLanguage } from '../context/LanguageContext';
import { buildExamOverviewPath } from '../utils/contentRoutes';
import { accentRuleSx, CONTENT_MAX_WIDTH } from '../styles/tokens';
import { bandSx } from './home/homeUi';

const INSTALL_PLATFORM_OPTIONS = [
  { key: 'ios', label: 'iPhone / iPad' },
  { key: 'android', label: 'Android' },
  { key: 'desktop', label: 'Desktop' },
];

const isStandaloneDisplayMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const standaloneMatch =
    typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)')?.matches === true;
  const iosStandalone = typeof window.navigator !== 'undefined' && window.navigator.standalone === true;

  return standaloneMatch || iosStandalone;
};

export const detectInstallFaqPlatform = () => {
  if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
    return null;
  }

  const userAgent = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';
  const maxTouchPoints = window.navigator.maxTouchPoints || 0;
  const isAndroid = /Android/i.test(userAgent);
  const isIPhoneOrIPad = /iPhone|iPad|iPod/i.test(userAgent);
  const isTouchMac = /Mac/i.test(platform) && maxTouchPoints > 1;

  if (isIPhoneOrIPad || isTouchMac) {
    return 'ios';
  }

  if (isAndroid) {
    return 'android';
  }

  if (/Mobi|Mobile/i.test(userAgent)) {
    return null;
  }

  return 'desktop';
};

const InstallSection = ({ title, children, isInstalled = false }) => (
  <Box
    sx={{
      border: '1px solid #e0eaf5',
      borderRadius: 1,
      px: { xs: 2, md: 2.5 },
      py: { xs: 1.75, md: 2 },
      bgcolor: 'background.paper',
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
      {title}
    </Typography>
    {isInstalled && (
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', mb: 1.25 }}>
        This web app already looks installed on your device.
      </Typography>
    )}
    <Stack spacing={1}>{children}</Stack>
  </Box>
);

const inlineMenuIconSx = {
  fontSize: '1rem',
  verticalAlign: 'text-bottom',
  mx: 0.25,
};

const InstallWebAppAnswer = () => {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setSelectedPlatform(detectInstallFaqPlatform());
    setInstalled(isStandaloneDisplayMode());
  }, []);

  const handlePlatformToggle = (platformKey) => {
    setSelectedPlatform((prev) => (prev === platformKey ? null : platformKey));
  };

  const visiblePlatforms = selectedPlatform ? [selectedPlatform] : INSTALL_PLATFORM_OPTIONS.map((option) => option.key);

  return (
    <Box>
      <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
        We do not have a native iPhone app, Android app, or desktop app. On some devices and browsers, you can still
        install FIDE Prep as a web app and open it like an app.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.25, fontWeight: 600 }}>
          Show steps for your device:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {INSTALL_PLATFORM_OPTIONS.map((option) => (
            <Button
              key={option.key}
              variant={selectedPlatform === option.key ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => handlePlatformToggle(option.key)}
              endIcon={selectedPlatform === option.key ? <CloseIcon fontSize="small" /> : null}
              sx={{ px: 2.5, textTransform: 'none', fontWeight: 700 }}
            >
              {option.label}
            </Button>
          ))}
        </Box>
      </Box>

      <Stack spacing={1.5}>
        {visiblePlatforms.includes('ios') && (
          <InstallSection title="iPhone / iPad" isInstalled={installed && selectedPlatform === 'ios'}>
            <Typography variant="body2">
              1. Open <a href="/">FIDE Prep</a> in Safari.
            </Typography>
            <Typography variant="body2">
              2. Tap the Share button (it is often inside the menu
              <MoreHorizIcon sx={inlineMenuIconSx} />
              on bottom left).
            </Typography>
            <Typography variant="body2">3. Scroll down and tap "Add to Home Screen".</Typography>
            <Typography variant="body2">4. Tap Add to finish.</Typography>
          </InstallSection>
        )}

        {visiblePlatforms.includes('android') && (
          <InstallSection title="Android" isInstalled={installed && selectedPlatform === 'android'}>
            <Typography variant="body2">
              1. Open <a href="/">FIDE Prep</a> in Chrome.
            </Typography>
            <Typography variant="body2">
              2. Tap the browser menu
              <MoreVertIcon sx={inlineMenuIconSx} />
              in the top-right corner.
            </Typography>
            <Typography variant="body2">3. Tap "Add to Home screen" or "Install app".</Typography>
            <Typography variant="body2">4. Confirm the install.</Typography>
          </InstallSection>
        )}

        {visiblePlatforms.includes('desktop') && (
          <InstallSection title="Desktop" isInstalled={installed && selectedPlatform === 'desktop'}>
            <Typography variant="body2">
              1. Open <a href="/">FIDE Prep</a> in Chrome on your computer.
            </Typography>
            <Typography variant="body2">2. Look for the install icon on the right side of the address bar.</Typography>
            <Typography variant="body2">3. Click the install button.</Typography>
            <Typography variant="body2">4. Confirm to add the web app to your computer.</Typography>
          </InstallSection>
        )}
      </Stack>

      <Typography variant="body2" sx={{ mt: 1.75, color: 'text.secondary' }}>
        Browser menus and button names can be a little different depending on the browser and device.
      </Typography>
    </Box>
  );
};

const renderFaqAnswer = (faq, language) => {
  if (faq.type === 'install_web_app') {
    return <InstallWebAppAnswer />;
  }

  return (
    <>
      <Typography variant="body1" color="text.primary">
        {faq.answer}
      </Typography>
      {faq.link && (
        <Typography variant="body1" sx={{ mt: 1.25 }}>
          <a href={faq.link.href} target="_blank" rel="nofollow noreferrer">
            {faq.link.label}
          </a>
        </Typography>
      )}
      {faq.relatedLinks?.map(({ label, section }) => (
        <Typography key={label} variant="body1" sx={{ mt: 1.25 }}>
          <a href={`${buildExamOverviewPath(language)}${section ? `#${section}` : ''}`}>{label}</a>
        </Typography>
      ))}
    </>
  );
};

const FAQSection = ({ tone }) => {
  const { language } = useLanguage();
  const [expandedIndex, setExpandedIndex] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      setFaqs(faqData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading FAQ data:', err);
      setError('Failed to load FAQ data. Please try again later.');
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box component="section" sx={bandSx(tone)}>
      {/* Shares the container below so the heading, rule and first question all
          start on the same line. */}
      <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', mb: { xs: 4, md: 6 } }}>
        <Typography variant="h3" component="h2" fontWeight={900} color="primary.main" sx={{ mb: 1.5 }}>
          FAQ
        </Typography>
        <Box aria-hidden sx={{ ...accentRuleSx, mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
          Answers to common questions about the fide test in Switzerland.
        </Typography>
      </Box>
      <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto' }}>
        {faqs.map((faq, idx) => (
          <Box key={faq.question} sx={{ mb: { xs: 1.5, md: 2 } }}>
            <Box
              onClick={() => setExpandedIndex(expandedIndex === idx ? false : idx)}
              sx={{
                cursor: 'pointer',
                px: 3,
                py: 2,
                border: '1px solid #e0eaf5',
                borderBottomLeftRadius: expandedIndex === idx ? 0 : 8,
                borderBottomRightRadius: expandedIndex === idx ? 0 : 8,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease-in-out',
                bgcolor: 'background.paper',
                fontWeight: 700,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {faq.icon && (
                <span style={{ fontSize: 28, lineHeight: 1, marginRight: 16 }} role="img" aria-label={faq.icon}>
                  {faq.icon}
                </span>
              )}
              <span style={{ flex: 1 }}>{faq.question}</span>
              <ExpandMoreIcon
                sx={{
                  transform: expandedIndex === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>
            <Collapse in={expandedIndex === idx} timeout="auto">
              <Box
                sx={{
                  bgcolor: 'background.default',
                  color: 'text.primary',
                  px: 3,
                  pt: 2,
                  pb: 2.5,
                  mt: 0,
                  border: '1px solid #e0eaf5',
                  borderTop: 'none',
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  fontSize: '1.08rem',
                  boxShadow: 1,
                  position: 'relative',
                  zIndex: 1,
                  transition: 'all 0.2s ease-in-out',
                  transform: 'translateY(0)',
                }}
              >
                {renderFaqAnswer(faq, language)}
              </Box>
            </Collapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FAQSection;
